import { Check, X } from "lucide-react";
import {
  PASSWORD_RULES,
  getPasswordValidations,
  getPasswordRuleLabel,
  getPasswordScore,
} from "../utils/passwordPolicy";

/**
 * Live checklist of the password rules from utils/passwordPolicy, shared by the
 * emailed reset/activation page and the "Change Password" modal in Settings so
 * both flows show the user the exact same requirements.
 *
 * Layout is direction-agnostic on purpose: no row-reverse / margin flipping —
 * the surrounding `direction: rtl` mirrors the flex rows natively.
 */
const PasswordRequirements = ({
  password = "",
  language = "en",
  theme = "light",
  title,
  showMeter = true,
  style,
}) => {
  const isDark = theme === "dark";
  const validations = getPasswordValidations(password);
  const score = getPasswordScore(password);
  const total = PASSWORD_RULES.length;
  const isRTL = language === "ar";

  const meterColor =
    score === total ? "#10b981" : score >= 3 ? "#f59e0b" : "#ef4444";

  const meterLabel =
    score === total
      ? isRTL ? "قوية" : "Strong"
      : score >= 3
        ? isRTL ? "متوسطة" : "Medium"
        : isRTL ? "ضعيفة" : "Weak";

  return (
    <div
      style={{
        background: isDark ? "rgba(15, 23, 42, 0.45)" : "rgba(241, 245, 249, 0.85)",
        border: isDark
          ? "1px solid rgba(100, 116, 139, 0.35)"
          : "1px solid rgba(148, 163, 184, 0.3)",
        borderRadius: "14px",
        padding: "14px 16px",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        ...style,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
        }}
      >
        <span
          style={{
            fontSize: "13px",
            fontWeight: 700,
            color: isDark ? "rgba(255, 255, 255, 0.85)" : "#334155",
          }}
        >
          {title || (isRTL ? "يجب أن تحتوي كلمة المرور على:" : "Password must contain:")}
        </span>

        {showMeter && password.length > 0 && (
          <span style={{ fontSize: "12px", fontWeight: 700, color: meterColor }}>
            {meterLabel}
          </span>
        )}
      </div>

      {showMeter && (
        <div
          style={{
            display: "flex",
            gap: "4px",
            width: "100%",
          }}
        >
          {PASSWORD_RULES.map((rule, index) => (
            <span
              key={rule.key}
              style={{
                flex: 1,
                height: "4px",
                borderRadius: "999px",
                background:
                  index < score
                    ? meterColor
                    : isDark
                      ? "rgba(100, 116, 139, 0.35)"
                      : "rgba(148, 163, 184, 0.35)",
                transition: "background 0.25s ease",
              }}
            />
          ))}
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: "8px 16px",
        }}
      >
        {PASSWORD_RULES.map((rule) => {
          const valid = validations[rule.key];
          return (
            <div
              key={rule.key}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "12.5px",
                lineHeight: 1.5,
                color: valid
                  ? "#10b981"
                  : isDark
                    ? "rgba(255, 255, 255, 0.55)"
                    : "#64748b",
                transition: "color 0.2s ease",
              }}
            >
              <span
                style={{
                  width: "18px",
                  height: "18px",
                  borderRadius: "50%",
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: valid
                    ? "rgba(16, 185, 129, 0.15)"
                    : isDark
                      ? "rgba(100, 116, 139, 0.25)"
                      : "rgba(148, 163, 184, 0.2)",
                  transition: "background 0.2s ease",
                }}
              >
                {valid ? <Check size={12} strokeWidth={3} /> : <X size={12} strokeWidth={3} />}
              </span>
              <span>
                {getPasswordRuleLabel(rule, language)}
                {rule.hint && (
                  <span dir="ltr" style={{ marginInlineStart: "4px", unicodeBidi: "isolate" }}>
                    {rule.hint}
                  </span>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PasswordRequirements;
